import { useEffect, useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import axios from '../../lib/axios'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Send,
  Clock,
  AlertCircle,
  CheckCircle,
  Play,
  Pause,
  Trophy,
  X,
  ArrowRight,
  Zap,
  Mail,
  XCircle,
  Filter
} from 'lucide-react'
import '../Pages.css'
import './Calendar.css'

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAYS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const VIEW_OPTIONS = [
  { key: 'week', label: '7 Days' },
  { key: '15days', label: '15 Days' },
  { key: 'month', label: 'Month' },
]

// ── Helpers ──
const formatDateStr = (d) => {
  const yy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

const addDays = (d, n) => {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

const startOfWeek = (d) => {
  const r = new Date(d)
  r.setDate(r.getDate() - r.getDay())
  return r
}

const isSameDay = (a, b) => formatDateStr(a) === formatDateStr(b)

export default function CampaignCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [calendarData, setCalendarData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedDay, setSelectedDay] = useState(null)
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [view, setView] = useState('week')

  // ── Compute the visible date range based on view ──
  const { rangeStart, rangeEnd, visibleDays } = useMemo(() => {
    let start, end

    if (view === 'week') {
      start = startOfWeek(currentDate)
      end = addDays(start, 6)
    } else if (view === '15days') {
      start = startOfWeek(currentDate)
      end = addDays(start, 14)
    } else {
      // month
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth()
      const firstOfMonth = new Date(year, month, 1)
      const lastOfMonth = new Date(year, month + 1, 0)
      start = startOfWeek(firstOfMonth)
      end = addDays(startOfWeek(addDays(lastOfMonth, 6)), 6)
      // ensure exactly 42 days for a clean 6-row grid
      const diff = Math.round((end - start) / 86400000) + 1
      if (diff < 42) end = addDays(start, 41)
      if (diff > 42) end = addDays(start, 41)
    }

    const days = []
    let cursor = new Date(start)
    while (cursor <= end) {
      days.push(new Date(cursor))
      cursor = addDays(cursor, 1)
    }

    return { rangeStart: start, rangeEnd: end, visibleDays: days }
  }, [currentDate, view])

  // ── Fetch window: pad a bit around visible range ──
  const fetchStart = useMemo(() => formatDateStr(addDays(rangeStart, -5)), [rangeStart])
  const fetchEnd = useMemo(() => formatDateStr(addDays(rangeEnd, 5)), [rangeEnd])

  useEffect(() => {
    setLoading(true)
    axios
      .get(`email-campaign/calendar?start=${fetchStart}&end=${fetchEnd}`)
      .then((res) => {
        setCalendarData(res.data)
        const ids = new Set((res.data.campaigns || []).map(c => c.id))
        setSelectedCampaigns(ids)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [fetchStart, fetchEnd])

  // ── Navigation ──
  const navigate = (dir) => {
    setSelectedDay(null)
    if (view === 'week') setCurrentDate(prev => addDays(prev, dir * 7))
    else if (view === '15days') setCurrentDate(prev => addDays(prev, dir * 15))
    else {
      setCurrentDate(prev => {
        const d = new Date(prev)
        d.setMonth(d.getMonth() + dir)
        return d
      })
    }
  }

  const goToToday = () => {
    setSelectedDay(null)
    setCurrentDate(new Date())
  }

  // ── Events map ──
  const eventsByDate = useMemo(() => {
    if (!calendarData) return {}
    const map = {}
    for (const ev of calendarData.events) {
      if (!selectedCampaigns.has(ev.campaign_id)) continue
      if (!map[ev.date]) map[ev.date] = []
      map[ev.date].push(ev)
    }
    return map
  }, [calendarData, selectedCampaigns])

  const isScheduledDay = useCallback((dateStr, dayOfWeek) => {
    if (!calendarData) return false
    const dayName = WEEKDAYS_SHORT[dayOfWeek]
    return calendarData.schedule_windows?.some(w => {
      if (!selectedCampaigns.has(w.campaign_id)) return false
      if (!w.start_date || !w.end_date) return false
      if (w.days.length && !w.days.includes(dayName)) return false
      return dateStr >= w.start_date && dateStr <= w.end_date
    })
  }, [calendarData, selectedCampaigns])

  const todayStr = formatDateStr(new Date())
  const todayDate = new Date()

  const toggleCampaign = (id) => {
    setSelectedCampaigns(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const getDayEvents = (dateStr) => eventsByDate[dateStr] || []

  const selectedDayEvents = selectedDay ? getDayEvents(selectedDay) : []
  const selectedDateObj = selectedDay ? new Date(selectedDay + 'T00:00:00') : null

  // ── Title for toolbar ──
  const toolbarTitle = useMemo(() => {
    if (view === 'month') {
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    }
    const s = rangeStart
    const e = rangeEnd
    if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
      return `${s.getDate()} – ${e.getDate()} ${MONTHS[s.getMonth()]} ${s.getFullYear()}`
    }
    if (s.getFullYear() === e.getFullYear()) {
      return `${s.getDate()} ${MONTHS[s.getMonth()].slice(0, 3)} – ${e.getDate()} ${MONTHS[e.getMonth()].slice(0, 3)} ${s.getFullYear()}`
    }
    return `${s.getDate()} ${MONTHS[s.getMonth()].slice(0, 3)} ${s.getFullYear()} – ${e.getDate()} ${MONTHS[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`
  }, [view, currentDate, rangeStart, rangeEnd])

  // ── Grid rows ──
  const weeks = useMemo(() => {
    const rows = []
    for (let i = 0; i < visibleDays.length; i += 7) {
      rows.push(visibleDays.slice(i, i + 7))
    }
    // handle leftover (for 15-day, last row may have < 7)
    return rows
  }, [visibleDays])

  // ── Is a day in the "current" scope ──
  const isInScope = useCallback((d) => {
    if (view === 'month') return d.getMonth() === currentDate.getMonth()
    return true // week & 15day: all days are in scope
  }, [view, currentDate])

  // Max pills to show per cell
  const maxPills = view === 'week' ? 5 : view === '15days' ? 3 : 3

  if (loading) {
    return (
      <div className="page">
        <div className="calendar-loading">
          <div className="calendar-loading-pulse" />
          <p>Loading calendar...</p>
        </div>
      </div>
    )
  }

  const stats = calendarData?.stats || {}
  const campaigns = calendarData?.campaigns || []

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="header-info">
          <h2>Campaign Calendar</h2>
          <p>Visualize your campaign schedule and track email sending activity</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="cal-stats-row">
        <div className="cal-stat-card cal-stat-active">
          <div className="cal-stat-icon"><Zap size={20} /></div>
          <div className="cal-stat-content">
            <span className="cal-stat-value">{stats.active_campaigns || 0}</span>
            <span className="cal-stat-label">Active Campaigns</span>
          </div>
        </div>
        <div className="cal-stat-card cal-stat-pending">
          <div className="cal-stat-icon"><Clock size={20} /></div>
          <div className="cal-stat-content">
            <span className="cal-stat-value">{stats.total_pending || 0}</span>
            <span className="cal-stat-label">Emails Queued</span>
          </div>
        </div>
        <div className="cal-stat-card cal-stat-sent">
          <div className="cal-stat-icon"><Send size={20} /></div>
          <div className="cal-stat-content">
            <span className="cal-stat-value">{stats.total_sent || 0}</span>
            <span className="cal-stat-label">Emails Sent</span>
          </div>
        </div>
        <div className="cal-stat-card cal-stat-failed">
          <div className="cal-stat-icon"><AlertCircle size={20} /></div>
          <div className="cal-stat-content">
            <span className="cal-stat-value">{stats.total_failed || 0}</span>
            <span className="cal-stat-label">Failed</span>
          </div>
        </div>
      </div>

      <div className="cal-layout">
        {/* Calendar */}
        <div className="cal-main">
          {/* Toolbar */}
          <div className="cal-toolbar">
            <div className="cal-toolbar-left">
              <button className="cal-nav-btn" onClick={() => navigate(-1)}>
                <ChevronLeft size={18} />
              </button>
              <h3 className="cal-month-title">{toolbarTitle}</h3>
              <button className="cal-nav-btn" onClick={() => navigate(1)}>
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="cal-toolbar-right">
              {/* View switcher */}
              <div className="cal-view-switcher">
                {VIEW_OPTIONS.map(v => (
                  <button
                    key={v.key}
                    className={`cal-view-btn ${view === v.key ? 'active' : ''}`}
                    onClick={() => setView(v.key)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={goToToday}>
                <CalendarIcon size={14} /> Today
              </button>
              {campaigns.length > 0 && (
                <button
                  className={`btn btn-ghost btn-sm ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={14} /> Filter ({campaigns.length})
                </button>
              )}
            </div>
          </div>

          {/* Campaign filter chips */}
          {showFilters && campaigns.length > 0 && (
            <div className="cal-filter-bar">
              {campaigns.map(c => (
                <button
                  key={c.id}
                  className={`cal-filter-chip ${selectedCampaigns.has(c.id) ? 'selected' : ''}`}
                  style={{
                    '--chip-color': c.color,
                    borderColor: selectedCampaigns.has(c.id) ? c.color : undefined,
                    background: selectedCampaigns.has(c.id) ? `${c.color}12` : undefined,
                  }}
                  onClick={() => toggleCampaign(c.id)}
                >
                  <span className="cal-chip-dot" style={{ background: c.color }} />
                  {c.name}
                  <span className={`cal-chip-status ${c.status}`}>{c.status}</span>
                </button>
              ))}
            </div>
          )}

          {/* Calendar grid */}
          <div className={`cal-grid cal-grid-${view}`}>
            {/* Weekday headers */}
            {(view === 'week'
              ? WEEKDAYS_FULL
              : WEEKDAYS_SHORT
            ).map(d => (
              <div key={d} className="cal-weekday">{d}</div>
            ))}

            {/* Day cells */}
            {visibleDays.map((dayDate, i) => {
              const dateStr = formatDateStr(dayDate)
              const dayEvents = getDayEvents(dateStr)
              const lifecycleEvents = dayEvents.filter(e => e.type === 'lifecycle')
              const sendingEvents = dayEvents.filter(e => e.type === 'sending')
              const isToday = dateStr === todayStr
              const isSelected = dateStr === selectedDay
              const scheduled = isScheduledDay(dateStr, dayDate.getDay())
              const inScope = isInScope(dayDate)

              return (
                <div
                  key={i}
                  className={[
                    'cal-day',
                    `cal-day-${view}`,
                    !inScope && 'cal-day-outside',
                    isToday && 'cal-day-today',
                    isSelected && 'cal-day-selected',
                    scheduled && 'cal-day-scheduled',
                    dayEvents.length > 0 && 'cal-day-has-events',
                  ].filter(Boolean).join(' ')}
                  onClick={() => setSelectedDay(dateStr === selectedDay ? null : dateStr)}
                >
                  {/* Day header */}
                  <div className="cal-day-header">
                    <span className="cal-day-number">{dayDate.getDate()}</span>
                    {view === 'week' && (
                      <span className="cal-day-month-label">
                        {MONTHS[dayDate.getMonth()].slice(0, 3)}
                      </span>
                    )}
                    {view === '15days' && dayDate.getDate() === 1 && (
                      <span className="cal-day-month-label">
                        {MONTHS[dayDate.getMonth()].slice(0, 3)}
                      </span>
                    )}
                  </div>

                  {/* Event pills */}
                  <div className="cal-day-events">
                    {sendingEvents.slice(0, maxPills).map((ev, j) => (
                      <div
                        key={j}
                        className="cal-event-pill"
                        style={{ '--pill-color': ev.color }}
                        title={`${ev.campaign_name}: ${ev.total} emails`}
                      >
                        <span className="cal-pill-name">{ev.campaign_name}</span>
                        <span className="cal-pill-count">{ev.total}</span>
                      </div>
                    ))}
                    {sendingEvents.length > maxPills && (
                      <div className="cal-event-more">+{sendingEvents.length - maxPills} more</div>
                    )}
                  </div>

                  {/* Lifecycle dots */}
                  {lifecycleEvents.length > 0 && (
                    <div className="cal-lifecycle-dots">
                      {lifecycleEvents.map((ev, j) => (
                        <span
                          key={j}
                          className={`cal-lifecycle-dot cal-lifecycle-${ev.event}`}
                          title={`${ev.campaign_name} ${ev.event}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Legend */}
          <div className="cal-legend">
            <div className="cal-legend-item">
              <span className="cal-legend-dot" style={{ background: 'linear-gradient(135deg, #10b981, #34d399)' }} />
              <span>Active</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-dot" style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)' }} />
              <span>Paused</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-dot" style={{ background: 'linear-gradient(135deg, #6366f1, #818cf8)' }} />
              <span>Completed</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-dot" style={{ background: 'linear-gradient(135deg, #94a3b8, #cbd5e1)' }} />
              <span>Draft</span>
            </div>
            <div className="cal-legend-item">
              <span className="cal-legend-sched" />
              <span>Scheduled</span>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className={`cal-sidebar ${selectedDay ? 'open' : ''}`}>
          {selectedDay && (
            <>
              <div className="cal-sidebar-header">
                <div>
                  <h4>
                    {selectedDateObj?.toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </h4>
                  <p className="cal-sidebar-sub">
                    {selectedDayEvents.length === 0
                      ? 'No activity'
                      : `${selectedDayEvents.length} event${selectedDayEvents.length > 1 ? 's' : ''}`}
                  </p>
                </div>
                <button className="cal-sidebar-close" onClick={() => setSelectedDay(null)}>
                  <X size={16} />
                </button>
              </div>

              <div className="cal-sidebar-body">
                {selectedDayEvents.length === 0 ? (
                  <div className="cal-sidebar-empty">
                    <CalendarIcon size={44} strokeWidth={1} />
                    <p>No campaigns scheduled for this day</p>
                  </div>
                ) : (
                  <div className="cal-sidebar-events">
                    {selectedDayEvents.filter(e => e.type === 'lifecycle').map((ev, i) => (
                      <div key={`lc-${i}`} className={`cal-detail-card cal-detail-${ev.event}`}>
                        <div className="cal-detail-icon">
                          {ev.event === 'started' && <Play size={16} />}
                          {ev.event === 'paused' && <Pause size={16} />}
                          {ev.event === 'completed' && <Trophy size={16} />}
                        </div>
                        <div className="cal-detail-info">
                          <span className="cal-detail-title">{ev.campaign_name}</span>
                          <span className="cal-detail-event">
                            Campaign {ev.event}
                            {ev.pause_reason && <span className="cal-detail-reason"> — {ev.pause_reason}</span>}
                          </span>
                        </div>
                        <Link to={`/campaigns/analytics/${ev.campaign_id}`} className="cal-detail-link">
                          <ArrowRight size={14} />
                        </Link>
                      </div>
                    ))}

                    {selectedDayEvents.filter(e => e.type === 'sending').map((ev, i) => (
                      <div key={`send-${i}`} className="cal-detail-card cal-detail-sending">
                        <div className="cal-detail-icon" style={{ color: ev.color, background: `${ev.color}18` }}>
                          <Mail size={16} />
                        </div>
                        <div className="cal-detail-info">
                          <span className="cal-detail-title">{ev.campaign_name}</span>
                          <div className="cal-detail-counts">
                            {ev.counts.sent > 0 && (
                              <span className="cal-count cal-count-sent">
                                <CheckCircle size={12} /> {ev.counts.sent} sent
                              </span>
                            )}
                            {ev.counts.pending > 0 && (
                              <span className="cal-count cal-count-pending">
                                <Clock size={12} /> {ev.counts.pending} queued
                              </span>
                            )}
                            {ev.counts.failed > 0 && (
                              <span className="cal-count cal-count-failed">
                                <XCircle size={12} /> {ev.counts.failed} failed
                              </span>
                            )}
                            {ev.counts.cancelled > 0 && (
                              <span className="cal-count cal-count-cancelled">
                                <X size={12} /> {ev.counts.cancelled} cancelled
                              </span>
                            )}
                          </div>
                        </div>
                        <Link to={`/campaigns/analytics/${ev.campaign_id}`} className="cal-detail-link">
                          <ArrowRight size={14} />
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {!selectedDay && (
            <div className="cal-sidebar-empty">
              <CalendarIcon size={48} strokeWidth={1} />
              <p>Click on a day to see campaign details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
